import { Building2, Edit, ExternalLink } from "lucide-react";
import React from "react";

import { Button } from "../../../components/ui/button";

export function CompanyDetails({ company, onEdit }) {
	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-start justify-between">
				<div className="flex items-start space-x-4">
					{company.logo_url ? (
						<img
							src={company.logo_url}
							alt={`${company.company_name} logo`}
							className="h-16 w-16 rounded-lg object-cover"
						/>
					) : (
						<div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center">
							<Building2 className="h-8 w-8" />
						</div>
					)}
					<div>
						<h2 className="text-2xl font-bold">{company.company_name}</h2>
						<p className="text-muted-foreground">{company._id}</p>
						{company.company_industry && (
							<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground mt-2">
								{company.company_industry}
							</span>
						)}
					</div>
				</div>
				<Button onClick={onEdit} size="sm" className="text-gray-950">
					<Edit className="h-4 w-4 mr-2" />
					Edit
				</Button>
			</div>

			{/* Basic Information */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<div className="space-y-4">
					<h3 className="text-lg font-semibold">Basic Information</h3>

					{company.company_website && (
						<div>
							<label className="text-sm font-medium text-muted-foreground">Website</label>
							<div>
								<a
									href={company.company_website}
									target="_blank"
									rel="noopener noreferrer"
									className="text-primary hover:underline flex items-center gap-1"
								>
									{company.company_website.replace(/^https?:\/\//, "")}
									<ExternalLink className="h-3 w-3" />
								</a>
							</div>
						</div>
					)}

					{company.linkedin_url && (
						<div>
							<label className="text-sm font-medium text-muted-foreground">LinkedIn</label>
							<div>
								<a
									href={company.linkedin_url}
									target="_blank"
									rel="noopener noreferrer"
									className="text-primary hover:underline flex items-center gap-1"
								>
									View LinkedIn Profile
									<ExternalLink className="h-3 w-3" />
								</a>
							</div>
						</div>
					)}

					{company.headquarters && (
						<div>
							<label className="text-sm font-medium text-muted-foreground">Headquarters</label>
							<div className="text-sm">{company.headquarters}</div>
						</div>
					)}

					{company.founded && (
						<div>
							<label className="text-sm font-medium text-muted-foreground">Founded</label>
							<div className="text-sm">{company.founded}</div>
						</div>
					)}

					{company.type && (
						<div>
							<label className="text-sm font-medium text-muted-foreground">Company Type</label>
							<div className="text-sm">{company.type}</div>
						</div>
					)}

					{company.company_size_approx && (
						<div>
							<label className="text-sm font-medium text-muted-foreground">Company Size</label>
							<div className="text-sm">{company.company_size_approx} employees</div>
						</div>
					)}
				</div>

				<div className="space-y-4">
					<h3 className="text-lg font-semibold">Metrics & Funding</h3>

					{company.linkedin_followers_count && company.linkedin_followers_count !== "N/A" && (
						<div>
							<label className="text-sm font-medium text-muted-foreground">LinkedIn Followers</label>
							<div className="text-sm">{company.linkedin_followers_count}</div>
						</div>
					)}

					{company.funding && company.funding !== "N/A" && (
						<div>
							<label className="text-sm font-medium text-muted-foreground">Funding</label>
							<div className="text-sm">{company.funding}</div>
						</div>
					)}

					{company.funding_total_rounds && company.funding_total_rounds !== "N/A" && (
						<div>
							<label className="text-sm font-medium text-muted-foreground">Total Funding Rounds</label>
							<div className="text-sm">{company.funding_total_rounds}</div>
						</div>
					)}

					{company.last_funding_round && company.last_funding_round !== "N/A" && (
						<div>
							<label className="text-sm font-medium text-muted-foreground">Last Funding Round</label>
							<div className="text-sm">{company.last_funding_round}</div>
						</div>
					)}

					{company.funding_option && company.funding_option !== "N/A" && (
						<div>
							<label className="text-sm font-medium text-muted-foreground">Funding Option</label>
							<div className="text-sm">{company.funding_option}</div>
						</div>
					)}
				</div>
			</div>

			{/* Locations */}
			{company.locations && company.locations.length > 0 && (
				<div>
					<h3 className="text-lg font-semibold mb-2">Locations</h3>
					<div className="flex flex-wrap gap-2">
						{company.locations.map((location, index) => (
							<span
								key={index}
								className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground border"
							>
								{location}
							</span>
						))}
					</div>
				</div>
			)}

			{/* Specialties */}
			{company.specialties && (
				<div>
					<h3 className="text-lg font-semibold mb-2">Specialties</h3>
					<p className="text-sm text-muted-foreground">{company.specialties}</p>
				</div>
			)}

			{/* About Us Summary */}
			{company.about_us_summary && (
				<div>
					<h3 className="text-lg font-semibold mb-2">Summary</h3>
					<p className="text-sm">{company.about_us_summary}</p>
				</div>
			)}

			{/* About Us */}
			{company.about_us && (
				<div>
					<h3 className="text-lg font-semibold mb-2">About Us</h3>
					<div className="text-sm whitespace-pre-wrap">{company.about_us}</div>
				</div>
			)}

			{/* Timestamps */}
			{(company.createdAt || company.updatedAt) && (
				<div className="pt-4 border-t">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-muted-foreground">
						{company.createdAt && (
							<div>
								<span className="font-medium">Created:</span> {new Date(company.createdAt).toLocaleString()}
							</div>
						)}
						{company.updatedAt && (
							<div>
								<span className="font-medium">Last Updated:</span> {new Date(company.updatedAt).toLocaleString()}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
